import {
  ArrowGo,
  Check,
  Heart,
  HeartFilled,
  Share,
  UserFilled,
  Write,
} from "@assets"
import { Head, Link, router } from "@inertiajs/react"
import {
  ArrowDown,
  ArrowUp,
  BarChart2,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  MessageCircle,
  RotateCcw,
  Tag,
} from "lucide-react"
import { useEffect, useState } from "react"

import { AuthButton } from "@/components/auth-button"
import { ContentActionsMenu } from "@/components/content-actions-menu"
import { PageHeader } from "@/components/page-header"
import { RichTextEditor } from "@/components/rich-text-editor"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { CardContent, CardTitle } from "@/components/ui/card"
import { Dialog, DialogContent } from "@/components/ui/dialog"
import { Separator } from "@/components/ui/separator"
import { useAuth } from "@/hooks/use-auth"
import { useFavorites } from "@/hooks/use-favorites"
import AppLayout from "@/layouts/app-layout"
import {
  showBadgeToast,
  showChallengeToast,
  showPointsToast,
} from "@/lib/notifications"
import {
  forumPath,
  forumPostPath,
  forumPostsEditPath,
  userPath,
} from "@/routes"
interface ForumShowProps {
  post: {
    id: number
    title: string
    content: string
    created_at: string
    updated_at: string
    votes: number
    user_vote?: "up" | "down" | null
    is_resolved: boolean
    is_pinned: boolean
    views_count: number
    answer_count: number
    author: {
      id: number
      name: string
      verified: boolean
      avatar_url?: string | null
      reputation: number
    }
    category: {
      id: number
      name: string
      icon: string
      color: string
    }
    tags?: string[]
    is_favorited?: boolean
    favorites_count?: number
  }
  answers: {
    id: number
    content: string
    created_at: string
    votes: number
    user_vote?: "up" | "down" | null
    is_best_answer: boolean
    is_accepted: boolean
    author: {
      id: number
      name: string
      verified: boolean
      avatar_url?: string | null
      reputation: number
    }
  }[]
  answers_pagination?: {
    current_page: number
    total_pages: number
    per_page: number
    total_count: number
  }
  related_posts: {
    id: number | string
    title: string
    votes: number
    answer_count: number
  }[]
  can_edit?: boolean
}

type AnswerItem = ForumShowProps["answers"][number]

export default function ForumShow({
  post,
  answers,
  answers_pagination,
  related_posts,
  can_edit = false,
}: ForumShowProps) {
  const { user } = useAuth()
  const [newAnswer, setNewAnswer] = useState("")
  const [formResetKey, setFormResetKey] = useState<number>(0)
  const [isAnswerModalOpen, setIsAnswerModalOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [votingTarget, setVotingTarget] = useState<string | null>(null)
  const [answersState, setAnswersState] = useState<AnswerItem[]>(answers)
  const [answerCount, setAnswerCount] = useState(post.answer_count)
  const [postVotes, setPostVotes] = useState(post.votes)
  const [postUserVote, setPostUserVote] = useState<typeof post.user_vote>(
    post.user_vote ?? null,
  )
  const [postAuthorReputation, setPostAuthorReputation] = useState<number>(
    post.author.reputation,
  )
  const [postIsResolved, setPostIsResolved] = useState<boolean>(
    post.is_resolved,
  )
  const [editingAnswerId, setEditingAnswerId] = useState<number | null>(null)
  const [editingContent, setEditingContent] = useState<string>("")
  const [savingAnswerId, setSavingAnswerId] = useState<number | null>(null)
  const [deletingAnswerId, setDeletingAnswerId] = useState<number | null>(null)
  const [acceptingAnswerId, setAcceptingAnswerId] = useState<number | null>(
    null,
  )
  const [editingError, setEditingError] = useState<string | null>(null)

  // Sync local state with Inertia props (pagination changes, router.reload())
  useEffect(() => {
    setAnswersState(answers)
  }, [answers])

  useEffect(() => {
    setAnswerCount(post.answer_count)
  }, [post.answer_count])

  const {
    isFavorited: getStoreFavorite,
    setFavorite: setStoreFavorite,
    getFavoritesCount: getStoreCount,
    setFavoritesCount: setStoreCount,
  } = useFavorites()

  // Use store value if available, otherwise fallback to props
  const storedFavorite = getStoreFavorite("forum_post", post.id)
  const storedCount = getStoreCount("forum_post", post.id)
  const actualFavorited = storedFavorite ?? post.is_favorited
  const actualFavoritesCount = storedCount ?? post.favorites_count ?? 0
  // CSRF helper
  const getCsrf = () =>
    document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content") ?? ""

  // Pagination component
  const PaginationControls = ({
    currentPage,
    totalPages,
    onPrev,
    onNext,
    disablePrev,
    disableNext,
  }: {
    currentPage: number
    totalPages: number
    onPrev: () => void
    onNext: () => void
    disablePrev?: boolean
    disableNext?: boolean
  }) => (
    <div className="flex items-center justify-between gap-4">
      <Button
        variant="outline"
        size="sm"
        onClick={onPrev}
        disabled={disablePrev ?? currentPage <= 1}
        className="flex items-center gap-2"
      >
        <ChevronLeft className="h-4 w-4" />
        Anterior
      </Button>
      <span className="text-muted-foreground text-sm">
        Página {currentPage} de {totalPages}
      </span>
      <Button
        variant="outline"
        size="sm"
        onClick={onNext}
        disabled={disableNext ?? currentPage >= totalPages}
        className="flex items-center gap-2"
      >
        Siguiente
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  )

  // Consider rich-text empty if it has no visible text and no media embeds
  const isHtmlEmpty = (html: string) => {
    const div = document.createElement("div")
    div.innerHTML = html || ""
    // Remove zero-width chars and trim
    const text = (div.textContent || "")
      .replace(/\u200B/g, "")
      .replace(/&nbsp;/g, " ")
      .trim()
    // If there is any media/content element, consider it non-empty
    const hasMedia =
      div.querySelector("img, video, audio, iframe, figure, attachment") !==
      null
    return text.length === 0 && !hasMedia
  }

  // Inline edit/delete handlers for answers
  const startEditAnswer = (answer: AnswerItem) => {
    setEditingAnswerId(answer.id)
    setEditingContent(answer.content || "")
    setEditingError(null)
  }

  const cancelEditAnswer = () => {
    setEditingAnswerId(null)
    setEditingContent("")
    setEditingError(null)
  }

  const saveEditAnswer = (answerId: number) => {
    const current = answersState.find((a) => a.id === answerId)
    const isUnchanged = current ? current.content === editingContent : false
    if (isHtmlEmpty(editingContent) || isUnchanged || savingAnswerId) return
    setSavingAnswerId(answerId)
    setEditingError(null)
    const csrf = getCsrf()
    fetch(`/foro/posts/${post.id}/respuestas/${answerId}`, {
      method: "PATCH",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": csrf,
      },
      body: JSON.stringify({ forum_answer: { content: editingContent } }),
    })
      .then(async (res) => {
        if (!res.ok) {
          const msg = await res.text()
          throw new Error(msg || "No se pudo actualizar la respuesta")
        }
        const data = (await res.json()) as {
          message: string
          answer: AnswerItem
        }
        setAnswersState((prev) =>
          prev.map((a) =>
            a.id === answerId ? { ...a, content: data.answer.content } : a,
          ),
        )
        setEditingAnswerId(null)
        setEditingContent("")
      })
      .catch((err: unknown) => {
        const msg =
          err instanceof Error
            ? err.message
            : "Error desconocido al guardar la respuesta"
        setEditingError(msg)
        console.error("Guardar respuesta fallo:", msg)
      })
      .finally(() => setSavingAnswerId(null))
  }

  const deleteAnswer = (answerId: number) => {
    if (deletingAnswerId) return
    if (!confirm("¿Eliminar esta respuesta?")) return
    setDeletingAnswerId(answerId)
    const csrf = getCsrf()
    fetch(`/foro/posts/${post.id}/respuestas/${answerId}`, {
      method: "DELETE",
      headers: {
        Accept: "application/json",
        "X-CSRF-Token": csrf,
      },
    })
      .then(async (res) => {
        if (!res.ok) {
          const msg = await res.text()
          throw new Error(msg || "No se pudo eliminar la respuesta")
        }
        // Optimistic removal for immediate UI feedback
        setAnswersState((prev) => prev.filter((a) => a.id !== answerId))
        setAnswerCount((prev) => prev - 1)
        if (editingAnswerId === answerId) {
          setEditingAnswerId(null)
          setEditingContent("")
        }
        // Reload from server to get fresh pagination (total_pages, total_count)
        const currentPage = answers_pagination?.current_page ?? 1
        const perPage = answers_pagination?.per_page ?? 10
        // If this was the last answer on the current page, go back one page
        if (answersState.length <= 1 && currentPage > 1) {
          router.get(
            `/foro/posts/${post.id}`,
            { answers_page: currentPage - 1, answers_per_page: perPage },
            { preserveScroll: true },
          )
        } else {
          router.reload()
        }
      })
      .catch((err: unknown) => {
        const msg =
          err instanceof Error
            ? err.message
            : "Error desconocido al eliminar la respuesta"
        console.error("Eliminar respuesta fallo:", msg)
      })
      .finally(() => setDeletingAnswerId(null))
  }

  const acceptAnswer = (answerId: number) => {
    if (acceptingAnswerId) return
    setAcceptingAnswerId(answerId)
    const csrf = getCsrf()
    fetch(`/foro/posts/${post.id}/respuestas/${answerId}/accept`, {
      method: "PATCH",
      headers: {
        Accept: "application/json",
        "X-CSRF-Token": csrf,
      },
    })
      .then(async (res) => {
        if (!res.ok) {
          const msg = await res.text()
          throw new Error(msg || "No se pudo aceptar la respuesta")
        }
        const data = (await res.json()) as {
          message: string
          answer: AnswerItem
          post_resolved?: boolean
          badge_unlocked?: { name: string; emoji?: string; reason?: string }
          points_awarded?: { points: number; reason?: string }
        }
        // Update answers state: set all as not accepted, then mark this one as accepted
        setAnswersState((prev) => {
          const updated = prev.map((a) => ({
            ...a,
            is_best_answer: a.id === answerId,
            is_accepted: a.id === answerId,
          }))
          // Move accepted answer to top
          const accepted = updated.find((a) => a.id === answerId)
          const others = updated.filter((a) => a.id !== answerId)
          return accepted ? [accepted, ...others] : updated
        })
        // Update post resolved status if needed
        if (data.post_resolved) {
          setPostIsResolved(true)
        }
        // Show notifications for badges/points
        if (data.badge_unlocked) {
          showBadgeToast(data.badge_unlocked, user?.id)
        }
        if (data.points_awarded) {
          showPointsToast(data.points_awarded)
        }
      })
      .catch((err: unknown) => {
        const msg =
          err instanceof Error
            ? err.message
            : "Error desconocido al aceptar la respuesta"
        console.error("Aceptar respuesta fallo:", msg)
      })
      .finally(() => setAcceptingAnswerId(null))
  }

  const unacceptAnswer = (answerId: number) => {
    if (acceptingAnswerId) return
    setAcceptingAnswerId(answerId)
    const csrf = getCsrf()
    fetch(`/foro/posts/${post.id}/respuestas/${answerId}/unaccept`, {
      method: "PATCH",
      headers: {
        Accept: "application/json",
        "X-CSRF-Token": csrf,
      },
    })
      .then(async (res) => {
        if (!res.ok) {
          const msg = await res.text()
          throw new Error(msg || "No se pudo desmarcar la respuesta")
        }
        const data = (await res.json()) as {
          message: string
          post_reopened?: boolean
        }
        // Update answers state: remove accepted status
        setAnswersState((prev) =>
          prev.map((a) => ({
            ...a,
            is_best_answer: false,
            is_accepted: false,
          })),
        )
        // Update post resolved status if needed
        if (data.post_reopened) {
          setPostIsResolved(false)
        }
      })
      .catch((err: unknown) => {
        const msg =
          err instanceof Error
            ? err.message
            : "Error desconocido al desmarcar la respuesta"
        console.error("Desmarcar respuesta fallo:", msg)
      })
      .finally(() => setAcceptingAnswerId(null))
  }

  const formatDate = (dateString: string) => {
    return new Intl.DateTimeFormat("es-ES", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(dateString))
  }

  const formatRelativeDate = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInHours = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60 * 60),
    )

    if (diffInHours < 1) return "Hace menos de 1 hora"
    if (diffInHours < 24) return `Hace ${diffInHours} horas`

    const diffInDays = Math.floor(diffInHours / 24)
    if (diffInDays < 7) return `Hace ${diffInDays} días`

    return formatDate(dateString)
  }

  const handleFavorite = () => {
    const newValue = !actualFavorited
    const newCount = newValue
      ? actualFavoritesCount + 1
      : Math.max(0, actualFavoritesCount - 1)

    // Sync with global store immediately for optimistic update
    setStoreFavorite("forum_post", post.id, newValue)
    setStoreCount("forum_post", post.id, newCount)

    router.post(
      `/foro/posts/${post.id}/favorite`,
      {},
      {
        preserveScroll: true,
        preserveState: true,
        onError: () => {
          // Revert on error
          setStoreFavorite("forum_post", post.id, !newValue)
          setStoreCount(
            "forum_post",
            post.id,
            newValue ? Math.max(0, newCount - 1) : newCount + 1,
          )
        },
      },
    )
  }

  const handleShare = async () => {
    try {
      const url = window.location.href
      const title = post?.title ?? "Consulta"
      if (navigator.share) {
        await navigator.share({ title, url })
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(url)
      }
    } catch {
      // no-op
    }
  }

  const handleDelete = () => {
    if (
      !confirm("¿Eliminar esta publicación? Esta acción no se puede deshacer.")
    )
      return
    router.delete(`/foro/posts/${post.id}`, {
      preserveScroll: false,
      onSuccess: () => router.visit(forumPath()),
    })
  }

  const handleResolve = () => {
    router.patch(
      `/foro/posts/${post.id}/resolve`,
      {},
      {
        preserveScroll: true,
        preserveState: true,
        onSuccess: () => {
          setPostIsResolved(true)
        },
      },
    )
  }

  const handleReopen = () => {
    router.patch(
      `/foro/posts/${post.id}/reopen`,
      {},
      {
        preserveScroll: true,
        preserveState: true,
        onSuccess: () => {
          setPostIsResolved(false)
        },
      },
    )
  }

  const handleSubmitAnswer = (e?: React.FormEvent) => {
    e?.preventDefault()
    // Submit new answer to nested answers route
    const content = newAnswer
    if (isHtmlEmpty(content) || isSubmitting) return

    setIsSubmitting(true)
    const csrf = getCsrf()
    fetch(`/foro/posts/${post.id}/respuestas`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": csrf,
      },
      body: JSON.stringify({ forum_answer: { content } }),
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(await res.text())
        const data = (await res.json()) as {
          message: string
          answer: AnswerItem
          badge_unlocked?: { name: string; emoji?: string; reason?: string }
          points_awarded?: { points: number; reason?: string }
          challenge_completed?: {
            title: string
            description: string
            points: number
            icon?: string
          }
        }
        setNewAnswer("")
        // Force remount of editor to clear any internal state
        setFormResetKey((k) => k + 1)
        setIsAnswerModalOpen(false)
        // If a badge was unlocked (10+ answers), show immediate toast since this flow doesn't navigate via Inertia
        if (data.badge_unlocked) {
          showBadgeToast(data.badge_unlocked, user?.id)
        }
        // Mostrar toast de puntos al responder (solo se desactiva para votos)
        if (data.points_awarded) {
          showPointsToast(data.points_awarded)
        }
        // Mostrar toast si se completó un desafío semanal
        if (data.challenge_completed) {
          showChallengeToast(data.challenge_completed)
        }
        // Reload from server to get fresh answers list and pagination
        // New answers appear on page 1 (ordered newest first)
        const currentPage = answers_pagination?.current_page ?? 1
        const perPage = answers_pagination?.per_page ?? 10
        if (currentPage === 1) {
          router.reload()
        } else {
          router.get(
            `/foro/posts/${post.id}`,
            { answers_page: 1, answers_per_page: perPage },
            { preserveScroll: true },
          )
        }
      })
      .catch((err) => {
        console.error("[ForumAnswer] Error creating answer:", err)
      })
      .finally(() => setIsSubmitting(false))
  }

  const handleVote = (
    type: "up" | "down",
    targetType: "post" | "answer",
    targetId: number,
  ) => {
    // Prevent voting on own content (additional frontend guard; backend enforces too)
    if (targetType === "post" && user?.id === post.author.id) return
    if (targetType === "answer") {
      const a = answersState.find((x) => x.id === targetId)
      if (a && user?.id === a.author.id) return
    }
    const key = `${targetType}-${targetId}`
    if (votingTarget === key) return

    // Compute optimistic changes
    // - voteDelta: para el contador de votos (1, 0, -1)
    // - repDelta: para la reputación (0 o múltiplos de 10)
    const applyDelta = (
      current: "up" | "down" | null | undefined,
      next: "up" | "down",
    ): {
      voteDelta: number
      repDelta: number
      newVote: "up" | "down" | null
    } => {
      // Si se hace clic en el mismo voto, se quita
      if (current === next) {
        return {
          voteDelta: next === "up" ? -1 : +1, // Revertir el voto actual
          repDelta: next === "up" ? -10 : 0, // Solo quitamos reputación si era upvote
          newVote: null,
        }
      }

      // Si no hay voto previo
      if (current == null) {
        return {
          voteDelta: next === "up" ? +1 : -1,
          repDelta: next === "up" ? +10 : 0, // No restar reputación por downvote inicial
          newVote: next,
        }
      }

      // Cambiando entre up y down
      if (current === "up" && next === "down") {
        // Cambio de up a down: -2 en votos y -10 en reputación (solo quitamos el upvote)
        return {
          voteDelta: -2,
          repDelta: -10,
          newVote: "down",
        }
      } else {
        // Cambio de down a up: +2 en votos y +10 en reputación
        return {
          voteDelta: +2,
          repDelta: +10,
          newVote: "up" as const,
        }
      }
    }

    let rollback: (() => void) | null = null

    if (targetType === "post") {
      const { voteDelta, repDelta, newVote } = applyDelta(postUserVote, type)
      const prevVotes = postVotes
      const prevUserVote = postUserVote
      const prevAuthorRep = postAuthorReputation

      // Actualizar contador de votos (1 en 1)
      setPostVotes((prev) => prev + voteDelta)
      // Actualizar el voto del usuario
      setPostUserVote(newVote)
      // Actualizar reputación (10 en 10)
      setPostAuthorReputation((prev) => prev + repDelta)

      rollback = () => {
        setPostVotes(prevVotes)
        setPostUserVote(prevUserVote)
        setPostAuthorReputation(prevAuthorRep)
      }
    } else {
      setAnswersState((prev) => {
        return prev.map((a) => {
          if (a.id !== targetId) return a
          const { voteDelta, repDelta, newVote } = applyDelta(a.user_vote, type)
          const updated: AnswerItem = {
            ...a,
            votes: a.votes + voteDelta,
            user_vote: newVote,
            author: { ...a.author, reputation: a.author.reputation + repDelta },
          }
          // store rollback
          rollback = () => {
            setAnswersState((p2) =>
              p2.map((x) =>
                x.id === targetId
                  ? {
                      ...x,
                      votes: a.votes,
                      user_vote: a.user_vote,
                      author: { ...x.author, reputation: a.author.reputation },
                    }
                  : x,
              ),
            )
          }
          return updated
        })
      })
    }

    setVotingTarget(key)
    const csrf = getCsrf()
    const payload =
      targetType === "post"
        ? { vote_type: type, post_id: targetId }
        : { vote_type: type, answer_id: targetId }
    fetch(`/foro/votos`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": csrf,
      },
      body: JSON.stringify(payload),
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(await res.text())
        const data = (await res.json()) as {
          author_points?: number
          user_vote?: string | null
        }
        if (targetType === "post") {
          if (typeof data.author_points === "number") {
            setPostAuthorReputation(data.author_points)
          }
          if ("user_vote" in data) {
            setPostUserVote(data.user_vote as typeof postUserVote)
          }
        } else if (targetType === "answer") {
          if (typeof data.author_points === "number") {
            setAnswersState((prev) =>
              prev.map((a) =>
                a.id === targetId
                  ? {
                      ...a,
                      author: { ...a.author, reputation: data.author_points! },
                    }
                  : a,
              ),
            )
          }
          if ("user_vote" in data) {
            setAnswersState((prev) =>
              prev.map((a) =>
                a.id === targetId
                  ? { ...a, user_vote: data.user_vote as typeof a.user_vote }
                  : a,
              ),
            )
          }
        }
      })
      .catch(() => {
        // revert optimistic change
        rollback?.()
      })
      .finally(() => setVotingTarget(null))
  }

  return (
    <AppLayout
      title="Consulta"
      showEditButton={can_edit}
      showDeleteButton={can_edit}
      showBackButton={false}
      showShareButton={false}
      showFavoriteButton={false}
      onEdit={() => router.visit(forumPostsEditPath(post.id))}
      onDelete={handleDelete}
    >
      <Head title={post?.title ?? "Consulta"} />

      <div className="mx-0 max-w-6xl space-y-4 py-4 sm:mx-auto md:mx-auto md:space-y-6 md:p-6 lg:mx-auto xl:mx-auto">
        {/* Desktop Navigation */}
        <div className="hidden md:block">
          <PageHeader
            showBackButton={false}
            showEdit={can_edit}
            showDelete={can_edit}
            showShare={false}
            showFavorite={false}
            editHref={forumPostsEditPath(post.id)}
            onDelete={handleDelete}
          />
        </div>
        <div className="px-4">
          <div className="text-primary flex flex-wrap items-center gap-2 text-[13px] font-medium tracking-[0.13px]">
            <button
              onClick={() => window.history.back()}
              className="hover:underline"
            >
              <div className="flex items-center gap-1">
                <ArrowGo className="text-primary size-4 rotate-180 fill-current" />
                Foro
              </div>
            </button>
          </div>
          <span className="text-card-foreground bg-card h-[23px] rounded-[6px] px-[10px] py-[3px] text-[12px] leading-[0.24px] font-medium tracking-[0.24px]">
            {post.category.name}
          </span>
          <div className="flex items-start justify-between gap-2">
            <h1 className="text-chat-5 mt-[14px] text-[21px] leading-[28px] font-semibold tracking-[0px] break-words">
              {post.title}
            </h1>
            <ContentActionsMenu
              contentType="ForumPost"
              contentId={post.id}
              authorId={post.author.id}
              authorName={post.author.name}
            />
          </div>
          <div className="flex items-center gap-[14px]">
            <div className="flex items-center gap-2">
              {/* <Calendar className="h-4 w-4 fill-muted-foreground" /> */}
              <span className="text-muted-foreground h-[19px] text-[13px] leading-[20px] tracking-[0.2px]">
                {formatRelativeDate(post.created_at)}
              </span>
            </div>
          </div>

          <div className="text-muted-foreground mt-[11px] flex flex-wrap items-center gap-[16px] text-sm">
            <div className="flex items-center">
              <MessageCircle className="text-muted-foreground mr-1 h-4.5 w-auto" />
              <span className="text-muted-foreground h-[19px] text-[13px] leading-[20px] tracking-[0.20px]">
                {answerCount}
              </span>
            </div>
            <div className="flex items-center">
              <BarChart2 className="fill-muted-foreground mr-1 h-4.5 w-auto" />
              <span className="text-muted-foreground h-[19px] text-[13px] leading-[20px] tracking-[0.20px]">
                {post.views_count}
              </span>
            </div>
            <div className="flex items-center gap-[4px]">
              <AuthButton
                variant="ghost"
                size="icon"
                className="h-[14px] w-[16px]"
                onClick={() => void handleFavorite()}
                authAction="register"
              >
                {actualFavorited ? (
                  <HeartFilled className="text-destructive size-4.5 w-auto fill-current" />
                ) : (
                  <Heart className="size-4.5 w-auto" />
                )}
                <span className="sr-only">
                  {actualFavorited
                    ? "Quitar de favoritos"
                    : "Agregar a favoritos"}
                </span>
              </AuthButton>
              <span className="text-muted-foreground text-[13px] tracking-[0.20px]">
                {actualFavoritesCount}
              </span>
            </div>

            <Button
              variant="ghost"
              size="icon"
              className="h-[20px] w-[20px]"
              onClick={() => void handleShare()}
            >
              <Share className="size-5" />
              <span className="sr-only">Compartir</span>
            </Button>
            {post.is_resolved && (
              <Badge className="border-chart-5 text-chart-5 bg-card">
                <CheckCircle className="text-active mr-1 h-3 w-3" />
                Resuelto
              </Badge>
            )}
          </div>
        </div>
        {/* Post Header */}
        <div className="space-y-4 px-4 md:space-y-6">
          <div className="flex flex-wrap gap-2">
            {post.is_pinned && <Badge variant="outline">📌 Fijado</Badge>}

            {post.tags?.map((tag) => (
              <Badge key={tag} variant="outline">
                <Tag className="mr-1 h-3 w-3" />
                {tag}
              </Badge>
            ))}
          </div>
        </div>

        <div className="grid gap-4 md:gap-6 lg:grid-cols-4">
          <div className="min-w-0 space-y-4 md:space-y-6 lg:col-span-3">
            <CardContent className="p-0">
              <div className="flex flex-col justify-between gap-2 p-4 sm:flex-row sm:items-center">
                <div className="flex w-full flex-wrap justify-between gap-2">
                  <div className="flex items-center gap-3">
                    <Avatar className="size-12">
                      <AvatarImage
                        src={post.author.avatar_url ?? undefined}
                        alt={post.author.name}
                      />
                      <AvatarFallback>
                        <UserFilled className="size-9 fill-white" />
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <div className="flex items-center gap-2">
                        <Link
                          href={userPath(post.author.id)}
                          className="text-foreground font-semibold"
                        >
                          {post.author.name}
                        </Link>
                        {post.author.verified && (
                          <Check className="fill-primary h-4 w-4" />
                        )}
                      </div>
                      <div className="text-muted-foreground text-xs">
                        {postAuthorReputation} puntos de reputación
                      </div>
                    </div>
                  </div>
                  {can_edit && (
                    <div className="text-muted-foreground text-xs sm:text-right">
                      {postIsResolved ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleReopen}
                        >
                          <RotateCcw className="h-4 w-4 sm:mr-2" />
                          Volver a abrir
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleResolve}
                        >
                          <CheckCircle className="text-active h-4 w-4 sm:mr-2" />
                          Marcar resuelto
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="bg-card flex gap-3 p-4 sm:gap-4">
                {/* Voting */}
                <div className="flex flex-col items-center gap-2">
                  <span
                    title={
                      user?.id === post.author.id
                        ? "No puedes votar tu propio post"
                        : undefined
                    }
                  >
                    <AuthButton
                      variant="ghost"
                      size="sm"
                      onClick={() => handleVote("up", "post", post.id)}
                      className={postUserVote === "up" ? "text-green-600" : ""}
                      authAction="register"
                      disabled={
                        votingTarget === `post-${post.id}` ||
                        user?.id === post.author.id
                      }
                    >
                      <ArrowUp className="h-5 w-5" />
                    </AuthButton>
                  </span>
                  <span className="text-lg font-semibold">{postVotes}</span>
                  <span
                    title={
                      user?.id === post.author.id
                        ? "No puedes votar tu propio post"
                        : undefined
                    }
                  >
                    <AuthButton
                      variant="ghost"
                      size="sm"
                      onClick={() => handleVote("down", "post", post.id)}
                      className={postUserVote === "down" ? "text-red-600" : ""}
                      authAction="register"
                      disabled={
                        votingTarget === `post-${post.id}` ||
                        user?.id === post.author.id
                      }
                    >
                      <ArrowDown className="h-5 w-5" />
                    </AuthButton>
                  </span>
                </div>

                {/* Post Content */}
                <div className="min-w-0 flex-1 space-y-4">
                  <div
                    className="prose prose-sm text-foreground max-w-none overflow-x-hidden break-words"
                    dangerouslySetInnerHTML={{ __html: post.content }}
                  />
                </div>
              </div>
            </CardContent>

            {/* Answers */}
            <div className="">
              <h2 className="text-foreground mb-4 flex items-center gap-2 px-4 text-xl font-semibold">
                <MessageCircle className="h-5 w-5" />
                Respuestas ({answerCount})
              </h2>

              {answersState.map((answer) => (
                <div
                  key={answer.id}
                  className={
                    answer.is_best_answer
                      ? "border-green-500 bg-green-50/50"
                      : "border-chart-4 border-b"
                  }
                >
                  <CardContent className="p-4">
                    {/* Author Info */}
                    <div className="flex flex-col justify-between gap-2 sm:flex-row sm:items-center">
                      <div className="flex items-center gap-3">
                        <Avatar className="size-12">
                          <AvatarImage
                            src={answer.author.avatar_url ?? undefined}
                            alt={answer.author.name}
                          />
                          <AvatarFallback>
                            <UserFilled className="size-9 fill-white" />
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="flex items-center gap-2">
                            <Link
                              href={userPath(answer.author.id)}
                              className="text-foreground text-sm font-medium"
                            >
                              {answer.author.name}
                            </Link>
                            {answer.author.verified && (
                              <Check className="fill-primary h-4 w-4" />
                            )}
                            <div className="text-muted-foreground text-xs sm:text-right">
                              {formatRelativeDate(answer.created_at)}
                            </div>
                          </div>
                          <div className="text-muted-foreground text-xs">
                            {answer.author.reputation} puntos
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 sm:gap-4">
                      {/* Voting */}
                      <div className="flex flex-col items-center gap-2">
                        <span
                          title={
                            user?.id === answer.author.id
                              ? "No puedes votar tu propia respuesta"
                              : undefined
                          }
                        >
                          <AuthButton
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              handleVote("up", "answer", answer.id)
                            }
                            className={
                              answer.user_vote === "up" ? "text-green-600" : ""
                            }
                            authAction="register"
                            disabled={
                              votingTarget === `answer-${answer.id}` ||
                              user?.id === answer.author.id
                            }
                          >
                            <ArrowUp className="h-5 w-5" />
                          </AuthButton>
                        </span>
                        <span className="text-lg font-semibold">
                          {answer.votes}
                        </span>
                        <span
                          title={
                            user?.id === answer.author.id
                              ? "No puedes votar tu propia respuesta"
                              : undefined
                          }
                        >
                          <AuthButton
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              handleVote("down", "answer", answer.id)
                            }
                            className={
                              answer.user_vote === "down" ? "text-red-600" : ""
                            }
                            authAction="register"
                            disabled={
                              votingTarget === `answer-${answer.id}` ||
                              user?.id === answer.author.id
                            }
                          >
                            <ArrowDown className="h-5 w-5" />
                          </AuthButton>
                        </span>
                        {answer.is_best_answer && (
                          <Badge
                            variant="default"
                            className="mt-2 bg-green-500 text-xs text-white"
                          >
                            <CheckCircle className="mr-1 h-3 w-3" />
                            Mejor
                          </Badge>
                        )}
                      </div>

                      {/* Answer Content (view or inline edit) */}
                      <div className="min-w-0 flex-1 space-y-4">
                        {editingAnswerId === answer.id ? (
                          <div className="space-y-3">
                            <RichTextEditor
                              value={editingContent}
                              onChange={(html: string) =>
                                setEditingContent(html)
                              }
                              placeholder="Edita tu respuesta..."
                            />
                            {!!editingError && (
                              <div className="text-sm text-red-600">
                                {editingError}
                              </div>
                            )}
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="secondary"
                                onClick={cancelEditAnswer}
                              >
                                Cancelar
                              </Button>
                              <Button
                                onClick={() => saveEditAnswer(answer.id)}
                                disabled={
                                  isHtmlEmpty(editingContent) ||
                                  savingAnswerId === answer.id ||
                                  answersState.find((a) => a.id === answer.id)
                                    ?.content === editingContent
                                }
                              >
                                Guardar
                              </Button>
                              <Button
                                variant="destructive"
                                onClick={() => deleteAnswer(answer.id)}
                                disabled={deletingAnswerId === answer.id}
                              >
                                Eliminar
                              </Button>
                            </div>
                            <Separator />
                          </div>
                        ) : (
                          <>
                            <div
                              className="prose prose-sm text-foreground max-w-none overflow-x-hidden break-words"
                              dangerouslySetInnerHTML={{
                                __html: answer.content,
                              }}
                            />
                            {/* Author actions: edit/delete for own answers */}
                            <div className="flex justify-end gap-2">
                              {user?.id === answer.author.id && (
                                <>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => startEditAnswer(answer)}
                                  >
                                    Editar
                                  </Button>
                                  <Button
                                    variant="destructive"
                                    size="sm"
                                    onClick={() => deleteAnswer(answer.id)}
                                  >
                                    Eliminar
                                  </Button>
                                </>
                              )}
                              {/* Post author can accept/unaccept answers */}
                              {user?.id === post.author.id &&
                                user?.id !== answer.author.id && (
                                  <>
                                    {!answer.is_accepted ? (
                                      <Button
                                        variant="default"
                                        size="sm"
                                        onClick={() => acceptAnswer(answer.id)}
                                        disabled={
                                          acceptingAnswerId === answer.id
                                        }
                                        className="bg-green-600 hover:bg-green-700"
                                      >
                                        <CheckCircle className="mr-1 h-4 w-4" />
                                        {acceptingAnswerId === answer.id
                                          ? "Aceptando..."
                                          : "Aceptar respuesta"}
                                      </Button>
                                    ) : (
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() =>
                                          unacceptAnswer(answer.id)
                                        }
                                        disabled={
                                          acceptingAnswerId === answer.id
                                        }
                                        className="border-red-500 text-red-600 hover:bg-red-50"
                                      >
                                        <RotateCcw className="mr-1 h-4 w-4" />
                                        {acceptingAnswerId === answer.id
                                          ? "Desmarcando..."
                                          : "Desmarcar"}
                                      </Button>
                                    )}
                                  </>
                                )}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </div>
              ))}
            </div>

            {/* Add Answer Modal */}
            <Dialog
              open={isAnswerModalOpen}
              onOpenChange={setIsAnswerModalOpen}
            >
              <DialogContent
                showWelcomeLogo={false}
                className="mt-4 w-[360px] sm:w-[420px]"
              >
                <form onSubmit={handleSubmitAnswer} className="">
                  <CardTitle className="text-chart-5 spacing-[27px] mt-[16px] mb-[12px] h-[25px] text-center text-[18px] font-semibold">
                    Tu respuesta
                  </CardTitle>
                  <div className="mt-[20px] flex flex-col items-center gap-[8px]">
                    <span className="text-chart-5 spacing-[27px] mb-[8px] text-center text-[18px] font-semibold">
                      {post.title}
                    </span>
                    <RichTextEditor
                      key={formResetKey}
                      value={newAnswer}
                      onChange={setNewAnswer}
                      placeholder="Escribe tu respuesta aquí..."
                      minHeight="140px"
                    />
                  </div>
                  <div className="mt-[28px] flex items-center justify-between">
                    <div className="text-muted-foreground text-sm">
                      Sé respetuoso y ayuda a la comunidad
                    </div>
                    <AuthButton
                      type="submit"
                      authAction="register"
                      disabled={isHtmlEmpty(newAnswer) || isSubmitting}
                      className="h-[49px] rounded-[14px] px-[24px] py-[12px] text-[15px] font-normal tracking-[0.45px]"
                    >
                      {isSubmitting ? "Enviando..." : "Enviar respuesta"}
                    </AuthButton>
                  </div>
                </form>
              </DialogContent>
            </Dialog>

            {/* Add Answer Button */}
            <AuthButton
              className="h-[49px] w-full rounded-[14px] border-0 text-[15px] font-medium tracking-[0.45px]"
              onClick={() => setIsAnswerModalOpen(true)}
              authAction="register"
            >
              <Write className="size-6 fill-white" />
              Escribir Respuesta
            </AuthButton>

            {/* Pagination */}
            {answers_pagination && answers_pagination.total_pages > 1 && (
              <div className="px-4">
                <PaginationControls
                  currentPage={answers_pagination.current_page}
                  totalPages={answers_pagination.total_pages}
                  disablePrev={answers_pagination.current_page <= 1}
                  disableNext={
                    answers_pagination.current_page >=
                    answers_pagination.total_pages
                  }
                  onPrev={() =>
                    router.get(
                      `/foro/posts/${post.id}`,
                      {
                        answers_page: answers_pagination.current_page - 1,
                        answers_per_page: answers_pagination.per_page,
                      },
                      { preserveState: true, preserveScroll: true },
                    )
                  }
                  onNext={() =>
                    router.get(
                      `/foro/posts/${post.id}`,
                      {
                        answers_page: answers_pagination.current_page + 1,
                        answers_per_page: answers_pagination.per_page,
                      },
                      { preserveState: true, preserveScroll: true },
                    )
                  }
                />
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="bg-card min-w-0 space-y-4 p-4 md:space-y-6">
            {/* Related Posts */}
            {related_posts && related_posts.length > 0 && (
              <div>
                <CardTitle className="text-lg">Posts relacionados</CardTitle>
                <CardContent>
                  <div className="">
                    {related_posts.slice(0, 5).map((relatedPost) => (
                      <Link
                        key={relatedPost.id}
                        href={forumPostPath(relatedPost.id)}
                      >
                        <div className="hover:bg-muted/50 cursor-pointer rounded p-2 transition-colors">
                          <h4 className="text-foreground mb-1 line-clamp-2 text-sm font-medium break-words">
                            {relatedPost.title}
                          </h4>
                          <div className="text-muted-foreground flex items-center gap-2 text-xs">
                            <span>{relatedPost.votes} votos</span>
                            <span>•</span>
                            <span>{relatedPost.answer_count} respuestas</span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </CardContent>
              </div>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  )
}
